/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { MixerHorizontalIcon, HomeIcon, PersonIcon } from '@/shared/ui/radix-icons';
import { FlexColumn, Icon, Text, Title, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { usePartnershipsWizard, type WizardSource } from '../context/PartnershipsWizardContext';
import { PropertyPicker } from './PropertyPicker';
import { ClientPicker } from './ClientPicker';
import { seedFromProperty, seedFromClient } from '../utils/seed-filters';
import { getClientInterests } from '@services/requests/housien-services/ClientInsightsService';
import { propertyHeadline, propertyCategory, propertyCityState } from '@/shared/utils/property-display';

type SourceCard = {
  value: WizardSource;
  titleKey: string;
  descKey: string;
  icon: React.FunctionComponent;
};

const CARDS: SourceCard[] = [
  { value: 'filter', titleKey: 'wizard.source_filter', descKey: 'wizard.source_filter_desc', icon: MixerHorizontalIcon },
  { value: 'property', titleKey: 'wizard.source_property', descKey: 'wizard.source_property_desc', icon: HomeIcon },
  { value: 'client', titleKey: 'wizard.source_client', descKey: 'wizard.source_client_desc', icon: PersonIcon },
];

/**
 * First wizard step where the user picks the search source (a reference
 * property, a client's preferences, or manual filters) and seeds the filters
 * accordingly before advancing.
 *
 * @returns The source-selection step.
 */
export function SourceStep() {
  const { t } = useTranslation('features/partnerships');
  const { agency_id } = useParams<{ agency_id: string }>();
  const setStep = usePartnershipsWizard((s) => s.setStep);
  const setSource = usePartnershipsWizard((s) => s.setSource);
  const setReferenceId = usePartnershipsWizard((s) => s.setReferenceId);
  const setReferenceLabel = usePartnershipsWizard((s) => s.setReferenceLabel);
  const setSeededFilters = usePartnershipsWizard((s) => s.setSeededFilters);

  const [pickerOpen, setPickerOpen] = useState<'property' | 'client' | null>(null);

  const handleCardClick = (value: WizardSource) => {
    setSource(value);
    if (value === 'filter') {
      setReferenceId(null);
      setReferenceLabel(null);
      setSeededFilters({});
      setStep('filters');
    } else if (value === 'property') {
      setPickerOpen('property');
    } else {
      setPickerOpen('client');
    }
  };

  const handlePropertySelected = useCallback((property: Record<string, unknown>) => {
    setReferenceId(property.id as string);
    const title = propertyHeadline(property) ?? propertyCategory(property) ?? '';
    const city = propertyCityState(property).city ?? '';
    setReferenceLabel(city ? `${title} - ${city}`.trim() : title);
    setSeededFilters(seedFromProperty(property));
    setPickerOpen(null);
    setStep('filters');
  }, [setReferenceId, setReferenceLabel, setSeededFilters, setStep]);

  const handleClientSelected = useCallback(async (client: Record<string, unknown>) => {
    setReferenceId(client.client_id as string);
    setReferenceLabel(client.full_name as string);

    let preferences: Record<string, unknown> | undefined;
    if (agency_id && client.client_id) {
      try {
        const interests = await getClientInterests(agency_id, client.client_id as string);
        if (interests.length > 0) {
          const intents = interests.map((i) => i.intent);
          const businessType = intents.includes('buy') ? 'sale' : intents.includes('lease') ? 'rental' : undefined;
          preferences = { ...(businessType ? { business_type: businessType } : {}) };
        }
      } catch { /* proceed without preferences */ }
    }

    const address = client.address as Record<string, unknown> | undefined;
    if (address?.city && !preferences?.cities) {
      preferences = { ...preferences, cities: [address.city as string] };
    }

    setSeededFilters(seedFromClient(client, preferences));
    setPickerOpen(null);
    setStep('filters');
  }, [agency_id, setReferenceId, setReferenceLabel, setSeededFilters, setStep]);

  return (
    <>
      <div className={cn(
        'flex flex-col gap-[var(--space-5)]',
        'rounded-(--radius-lg) border border-[var(--border-subtle)]',
        'bg-[var(--surface-primary)] p-[var(--space-6)]',
      )}>
        <Title order={2} size="sm">{t('wizard.choose_source')}</Title>
        <div className="grid grid-cols-1 gap-[var(--space-4)] sm:grid-cols-3">
          {CARDS.map((card) => (
            <UnstyledButton
              key={card.value}
              type="button"
              aria-label={t(card.titleKey)}
              onClick={() => handleCardClick(card.value)}
              className={cn(
                'flex flex-col items-center gap-[var(--space-3)] p-[var(--space-5)] text-center',
                'cursor-pointer rounded-(--radius-lg) transition-all duration-200',
                'hover:-translate-y-0.5 hover:shadow-[var(--shadow-lg)]',
                'bg-[var(--surface-primary)] border border-[var(--border-subtle)]',
              )}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--seed-light)]">
                <Icon as={card.icon} className="h-5 w-5 text-[var(--button-primary-bg)]" />
              </div>
              <FlexColumn className="gap-(--space-1)">
                <Text className="text-base font-semibold text-[var(--text-primary)]">
                  {t(card.titleKey)}
                </Text>
                <Text className="text-xs leading-relaxed text-[var(--text-secondary)]">
                  {t(card.descKey)}
                </Text>
              </FlexColumn>
            </UnstyledButton>
          ))}
        </div>
      </div>

      {pickerOpen === 'property' && agency_id && (
        <PropertyPicker
          agencyId={agency_id}
          onSelect={handlePropertySelected}
          onClose={() => setPickerOpen(null)}
        />
      )}
      {pickerOpen === 'client' && agency_id && (
        <ClientPicker
          agencyId={agency_id}
          onSelect={handleClientSelected}
          onClose={() => setPickerOpen(null)}
        />
      )}
    </>
  );
}
